import { useEffect, useState } from "react";
import { Bell, X, BellOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { isPushSupported, getPushStatus, subscribePush, sendTestPush } from "@/lib/push";

const DISMISS_KEY = "dsf_push_banner_dismissed_at";
const DISMISS_TTL_MS = 12 * 60 * 60 * 1000; // 12h — re-show banner once a day

export default function PushPermissionBanner() {
  const [status, setStatus] = useState("loading");
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      const t = localStorage.getItem(DISMISS_KEY);
      return !!(t && Date.now() - Number(t) < DISMISS_TTL_MS);
    } catch {
      return false;
    }
  });
  const [justEnabled, setJustEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const supported = await isPushSupported();
      if (!supported) {
        if (!cancelled) setStatus("unsupported");
        return;
      }
      const s = await getPushStatus();
      if (!cancelled) setStatus(s);
    })();
    return () => { cancelled = true; };
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await subscribePush();
      setStatus("subscribed");
      setJustEnabled(true);
      toast.success("Push notifications enabled on this device 🔔");
      // Auto-fire a test push so the user immediately sees one
      try {
        await sendTestPush();
        toast.success("Test notification sent — check your screen!");
      } catch {
        /* ignore test failure */
      }
      // Auto-hide success after 6s
      setTimeout(() => setJustEnabled(false), 6000);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to enable push";
      toast.error(msg);
      const next = await getPushStatus();
      setStatus(next);
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  // Hidden states
  if (status === "loading") return null;
  if (status === "subscribed" && !justEnabled) return null;
  if (status === "unsupported") return null; // No use prompting
  if (dismissed && status !== "denied") return null;

  if (justEnabled) {
    return (
      <div className="bg-[var(--dd-green)] text-white px-4 py-2.5 flex items-center justify-center gap-3 text-sm" data-testid="push-banner-success">
        <CheckCircle2 size={16} className="text-[var(--dd-mustard)]" />
        <span className="font-medium">Push notifications are active. You&apos;ll receive new order alerts even when the app is closed.</span>
      </div>
    );
  }

  if (status === "denied") {
    return (
      <div className="bg-[var(--dd-coral)]/10 border-y border-[var(--dd-coral)]/30 text-[var(--dd-coral)] px-4 py-2.5 flex items-center justify-between gap-3 text-sm" data-testid="push-banner-denied">
        <div className="flex items-center gap-2.5 min-w-0">
          <BellOff size={16} className="shrink-0" />
          <span className="truncate">
            Push notifications are <strong>blocked</strong> on this device. Open your browser settings, allow notifications for this site, then reload.
          </span>
        </div>
        <button onClick={dismiss} className="shrink-0 p-1 hover:bg-[var(--dd-coral)]/20 rounded-full" data-testid="push-banner-dismiss" aria-label="Dismiss">
          <X size={14} />
        </button>
      </div>
    );
  }

  // not_subscribed → call to action
  return (
    <div className="bg-[var(--dd-mustard)] text-[var(--dd-green-deep)] px-4 py-2.5 flex items-center justify-between gap-3 text-sm" data-testid="push-banner-cta">
      <div className="flex items-center gap-2.5 min-w-0">
        <Bell size={16} className="shrink-0 animate-pulse" />
        <span className="truncate">
          <strong>Enable push notifications</strong> on this device to receive new orders even when the app is closed.
          <span className="hidden md:inline text-[var(--dd-green-deep)]/70 ml-1">(Phone, tablet, laptop — one tap per device.)</span>
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={enable}
          disabled={busy}
          className="bg-[var(--dd-green)] text-white text-xs md:text-sm font-semibold px-3 md:px-4 py-1.5 rounded-full hover:bg-[var(--dd-green-deep)] transition disabled:opacity-50"
          data-testid="push-banner-enable"
        >
          {busy ? "Enabling…" : "Enable now"}
        </button>
        <button onClick={dismiss} className="p-1 hover:bg-black/10 rounded-full" data-testid="push-banner-dismiss" aria-label="Dismiss">
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
